// AirdropNotification — shown inside DiplomaModal after a successful mint.
// Variants: sent (amount + TXs), pending (queued), pro_required (upsell),
// already_claimed. Renders nothing for other/unknown errors — the diploma
// stays valid regardless of the airdrop.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ExternalLink, Loader2, Lock, CheckCircle2 } from "lucide-react";
import { API } from "../../lib/api";
import { useLanguage } from "../../i18n/LanguageContext";

const AIRDROP_AMOUNTS = { 0: 50000, 1: 50000, 2: 150000, 3: 150000, 4: 500000, 5: 500000 };

const T = {
  es: {
    title: "¡Airdrop recibido!", youReceived: "Has recibido", equivalent: "≈",
    viewTx: "Ver TX en Basescan", viewRBL: "Ver mis R₿L",
    pending: "Airdrop en proceso — lo recibirás en tu wallet en breve",
    alreadyClaimed: "Airdrop ya reclamado para este nivel",
    proRequired: "Airdrop de {n} R₿L disponible solo para usuarios PRO",
    proCta: "Hazte PRO",
  },
  en: {
    title: "Airdrop received!", youReceived: "You received", equivalent: "≈",
    viewTx: "View TX on Basescan", viewRBL: "View my R₿L",
    pending: "Airdrop processing — it will arrive in your wallet shortly",
    alreadyClaimed: "Airdrop already claimed for this level",
    proRequired: "{n} R₿L airdrop available for PRO users only",
    proCta: "Go PRO",
  },
  sk: {
    title: "Airdrop prijatý!", youReceived: "Dostali ste", equivalent: "≈",
    viewTx: "Zobraziť TX na Basescan", viewRBL: "Zobraziť môj R₿L",
    pending: "Airdrop sa spracúva — čoskoro dorazí do tvojej peňaženky",
    alreadyClaimed: "Airdrop už bol prijatý pre túto úroveň",
    proRequired: "Airdrop {n} R₿L je dostupný len pre PRO používateľov",
    proCta: "Získať PRO",
  }, de: { title: "Airdrop erhalten!", youReceived: "Du hast erhalten", equivalent: "≈", viewTx: "TX auf Basescan ansehen", viewRBL: "Meine R₿L ansehen", pending: "Airdrop wird verarbeitet — du erhältst ihn in Kürze in deiner Wallet", alreadyClaimed: "Airdrop für dieses Level bereits beansprucht", proRequired: "Airdrop von {n} R₿L nur für PRO-Nutzer verfügbar", proCta: "PRO werden" }, "zh-CN": { title: "空投已到账！", youReceived: "您已收到", equivalent: "≈", viewTx: "在 Basescan 查看交易", viewRBL: "查看我的 R₿L", pending: "空投处理中 — 您将很快在钱包中收到", alreadyClaimed: "此等级的空投已领取", proRequired: "仅限 PRO 用户领取 {n} R₿L 空投", proCta: "升级至 PRO" },
};

export default function AirdropNotification({ airdrop, tokenId }) {
  const { lang } = useLanguage();
  const t = T[lang] || T.en || T.es;
  const [priceUsd, setPriceUsd] = useState(null);

  useEffect(() => {
    fetch(`${API}/shadow/tokens/rbl-price`)
      .then((r) => r.json())
      .then((d) => setPriceUsd(d?.price_usd || null))
      .catch(() => {});
  }, []);

  if (!airdrop) return null;
  const amount = airdrop.amount_rbl || AIRDROP_AMOUNTS[tokenId] || 0;
  const usd = priceUsd ? (amount * priceUsd).toFixed(2) : null;

  if (airdrop.error === "pro_required") {
    return (
      <div className="mt-3 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] p-3" data-testid="airdrop-pro-required">
        <p className="text-xs text-amber-200">
          <Lock className="mr-1 inline h-3 w-3" />
          {t.proRequired.replace("{n}", amount.toLocaleString())}
        </p>
        <Link to="/academy" className="mt-1 inline-block text-[11px] font-black uppercase tracking-wider text-amber-300 underline">
          {t.proCta} →
        </Link>
      </div>
    );
  }
  if (airdrop.error === "already_claimed") {
    return (
      <p className="mt-3 text-center text-[11px] text-white/45" data-testid="airdrop-already-claimed">
        {t.alreadyClaimed}
      </p>
    );
  }
  if (airdrop.error) return null;

  if (airdrop.status === "pending") {
    return (
      <div className="mt-3 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] p-3 text-center" data-testid="airdrop-pending">
        <p className="text-xs text-amber-200">
          <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />
          <b>{amount.toLocaleString()} R₿L</b> · {t.pending}
        </p>
      </div>
    );
  }

  if (airdrop.status === "sent") {
    return (
      <div className="mt-3 rounded-lg border border-emerald-400/40 bg-emerald-400/10 p-4 text-center" data-testid="airdrop-success">
        <p className="text-sm font-black tracking-wide text-emerald-300">
          <Gift className="mr-1.5 inline h-4 w-4" /> 🎉 {t.title}
        </p>
        <p className="mt-1 text-xs text-emerald-200/80">{t.youReceived}:</p>
        <p className="font-mono text-2xl font-black text-white" data-testid="airdrop-amount">
          {amount.toLocaleString()} <span className="text-sm text-emerald-300">R₿L</span>
        </p>
        {usd && <p className="text-[11px] text-white/45">{t.equivalent} ${usd} USD</p>}
        <div className="mt-2 flex flex-col gap-1.5">
          {airdrop.airdrop_tx_hash && (
            <a href={`https://basescan.org/tx/${airdrop.airdrop_tx_hash}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 text-[11px] font-bold text-emerald-300 underline" data-testid="airdrop-tx-link">
              <ExternalLink className="h-3 w-3" /> {t.viewTx}
            </a>
          )}
          <Link to="/shadow/tokens" className="inline-flex items-center justify-center gap-1.5 text-[11px] font-bold text-white/65 underline hover:text-white">
            <CheckCircle2 className="h-3 w-3" /> {t.viewRBL}
          </Link>
        </div>
      </div>
    );
  }
  return null;
}
